/* eslint-disable react/prop-types */ // TODO: add props
import { useState, useEffect } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import EPT from 'edrolo/propTypes';
import urlResolver from 'edrolo/utils/URLResolver';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import { TabNav, GradientLoadingIndicator } from 'edrolo-style-guide';
import {
  QuestionSetNavBar,
  QuestionSetTopBar,
  TeacherQuestionView,
} from 'edrolo/domains/exams/components';
import {
  AnonymizerToggle,
  buildAnonymizer,
  useIsAnonymized,
} from 'edrolo/domains/anonymizer';
import { STATUS as EXAM_STATUS } from '../constants';
import StaffRibbon from 'edrolo/domains/app/components/StaffRibbon';
import { QuestionTypeSelection } from './components';
import {
  QuestionViewTab,
  NoResponsesMessage,
  QuestionHeader,
} from '../components';
import { useClassDetail } from 'edrolo/domains/bySchoolClass';
import style from './styles.scss';
import { ErrorAlert } from 'core/components';

const QuestionSetReview = ({
  exam,
  examStatus,
  questionId,
  question,
  responses,
  solution,
  nextQuestionId,
  prevQuestionId,
  classId,
  examId,
  location,
  highlightProfileId,
  getQuestionInstructionText,
  loadExam,
  loadExamSolution,
  loadClassQuestionResponses,
  searchParams,
}) => {
  const [tab, setTab] = useState('student-responses');
  const [docTitle, setDocTitle] = useState('');

  const {
    classDetail,
    loading: classDetailLoading,
    error: classDetailError,
  } = useClassDetail(classId);

  const { isAnonymized } = useIsAnonymized();

  const isLoading =
    !exam || !question || !solution || !responses || classDetailLoading;

  const error = classDetailError || examStatus === EXAM_STATUS.FAILURE;

  useEffect(() => {
    if (examStatus === EXAM_STATUS.UNINITIALIZED) {
      loadExam(examId);
    }

    loadExamSolution(examId);
    loadClassQuestionResponses(classId, examId, questionId);
  }, [
    examStatus,
    examId,
    questionId,
    classId,
    loadExam,
    loadExamSolution,
    loadClassQuestionResponses,
  ]);

  useEffect(() => {
    setDocTitle(
      !exam || !question
        ? 'Reviewing Question'
        : `Reviewing: ${exam.get('title')} - Q${question.get('full_serial')}`
    );
  }, [exam, question]);

  const buildQuestionSetReviewUrl = (examId, questionId, classId) => {
    const url = questionId
      ? urlResolver.reverse('question-set-question-review', {
          examId,
          questionId,
          classId,
        })
      : urlResolver.reverse('question-set-index', {
          examId,
          classId,
        });

    return searchParams ? `${url}?${searchParams.toString()}` : url;
  };

  const studentCourseProgUrl = (profileId) => {
    if (!exam) {
      return;
    }
    const url = urlResolver.reverse('student-course-progress', {
      classId: classId,
      profileId: profileId,
    });
    const coveredBranches = exam.get('covered_branches');
    return {
      pathname: url,
      state: {
        navigatedFrom: { url: location, label: `${exam.get('title')}` },
        highlightedLessonId: coveredBranches ? coveredBranches.get(0) : null,
      },
    };
  };

  const studentAnonymizer = buildAnonymizer({
    seedResolver: 'user_id',
    schemaFields: [
      { key: 'first_name', type: 'first_name' },
      { key: 'last_name', type: 'last_name' },
      { key: 'display_name', type: 'full_name' },
    ],
  });

  const classStudentProfiles = isAnonymized
    ? classDetail.get('students').map(studentAnonymizer)
    : classDetail.get('students');

  return (
    <>
      <AppContainer
        bodyClassName="lesson-questions"
        documentTitle={docTitle}
        renderTopBar={() => (
          <QuestionSetTopBar
            isTeacher
            classId={classId}
            exam={exam}
            examTitleIsLink
          />
        )}
        wide
      >
        <AnonymizerToggle />

        <div className={style.body}>
          {isLoading && !error && <GradientLoadingIndicator />}

          {error && (
            <div className={style.error}>
              <ErrorAlert />
            </div>
          )}

          {!isLoading && !error && (
            <>
              <QuestionSetNavBar
                nextQuestionUrl={buildQuestionSetReviewUrl(
                  exam.get('id'),
                  nextQuestionId,
                  classId
                )}
                previousQuestionUrl={buildQuestionSetReviewUrl(
                  exam.get('id'),
                  prevQuestionId,
                  classId
                )}
                questionSetIndexUrl={buildQuestionSetReviewUrl(
                  exam.get('id'),
                  null,
                  classId
                )}
                searchParams={searchParams}
                courseId={exam.get('course')}
              />
              <QuestionHeader
                classId={classId}
                examId={examId}
                question={question}
                instructionsText={getQuestionInstructionText(questionId)}
              />
              <TabNav
                onChange={(newTab) => setTab(newTab)}
                selectedItemId={tab}
                items={[
                  {
                    id: 'student-responses',
                    label: 'Student Responses',
                    disabled: !responses.size,
                  },
                  { id: 'question', label: 'View Question' },
                  { id: 'answer', label: 'View Answer' },
                ]}
              />
              <div className={style.tabContent}>
                {tab === 'student-responses' && !responses.size && (
                  <NoResponsesMessage />
                )}
                {tab === 'student-responses' &&
                  !!responses.size &&
                  !isLoading && (
                    <QuestionTypeSelection
                      question={question}
                      solution={solution}
                      responses={responses}
                      classStudentProfiles={classStudentProfiles}
                      highlightProfileId={highlightProfileId}
                      studentCourseProgUrl={studentCourseProgUrl}
                    />
                  )}
                {tab === 'question' && (
                  <QuestionViewTab
                    question={question}
                    exam={exam}
                    classId={classId}
                    onSubmitTeacherAnswer={() => setTab('answer')}
                  />
                )}
                {tab === 'answer' && (
                  <TeacherQuestionView
                    examId={examId}
                    questionId={questionId}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </AppContainer>
      <StaffRibbon
        classId={classId}
        links={[
          {
            title: 'Edit in CPT Tools',
            url: `/content/admin/exams/edit/${examId}/#question-${questionId}`,
          },
          {
            title: 'Edit in Admin',
            url: `/secret/exams/examquestion/${questionId}/change/`,
          },
        ]}
      />
    </>
  );
};

export default QuestionSetReview;

QuestionSetReview.propTypes = {
  classId: PT.oneOfType([PT.number, PT.string]).isRequired,
  questionId: PT.oneOfType([PT.number, PT.string]).isRequired,
  examId: PT.oneOfType([PT.number, PT.string]).isRequired,
  nextQuestionId: PT.oneOfType([PT.number, PT.string]),
  highlightProfileId: PT.number,
  exam: IPT.map,
  solution: IPT.map,
  question: IPT.contains({
    question_level: PT.number,
    full_serial: PT.string,
  }),
  responses: IPT.listOf(EPT.ExamResponse),
  examStatus: PT.oneOf(Object.values(EXAM_STATUS)),
  loadExam: PT.func.isRequired,
  loadExamSolution: PT.func.isRequired,
  loadClassQuestionResponses: PT.func.isRequired,
  searchParams: PT.object.isRequired,
};
